"use client";

import { BIRTH_FORM_LABELS } from "@/components/BirthDetailsForm.i18n";
import TrustStrip from "@/components/TrustStrip";

/**
 * Height-reserving placeholder for a deferred BirthDetailsForm-based tool
 * island. Rendered in two situations:
 *
 * 1. Pre-activation — as the interaction target that triggers loading the
 *    real chunk (`onActivate`), so the tool stays in the first viewport
 *    (rules 43/61) without shipping the heavy form stack in the route's
 *    initial JS.
 * 2. While the dynamic chunk loads (`next/dynamic` `loading:`) — same
 *    markup minus the activation handlers, so the swap never collapses to
 *    0px (rule 58 — never `loading: () => null` for a form/result surface).
 *
 * The shell mirrors BirthDetailsForm's TEXT, field for field, from the same
 * label source and the same props (heading, name, required time, trust
 * strip): the island swaps in 3.6 s after mount without any interaction, and
 * a placeholder with different words made the hydrated page read differently
 * from the server HTML, which the certification's word projection rejects.
 * `BirthToolFormShell.parity.test.tsx` pins the two word sets equal for every
 * calculator's prop set, so a label change on either side fails a test
 * instead of a certification.
 */
export interface BirthToolFormShellProps {
  language?: "en" | "hi";
  submitLabel?: string;
  onActivate?: () => void;
  /** Mirrors BirthDetailsForm `heading` and its position in the page outline. */
  heading?: string;
  headingLevel?: "h2" | "h3";
  /** Mirrors BirthDetailsForm `collectName` (default true). */
  collectName?: boolean;
  /** Mirrors BirthDetailsForm `optionalName`. */
  optionalName?: boolean;
  /** Mirrors BirthDetailsForm `requireBirthTime`. */
  requireBirthTime?: boolean;
  /** Mirrors BirthDetailsForm `showTrustStrip` (checkout badge off, like the form). */
  showTrustStrip?: boolean;
}

export default function BirthToolFormShell({
  language = "en",
  submitLabel,
  onActivate,
  heading,
  headingLevel: Heading = "h3",
  collectName = true,
  optionalName = false,
  requireBirthTime = false,
  showTrustStrip = false,
}: BirthToolFormShellProps) {
  const labels = BIRTH_FORM_LABELS[language];
  return (
    <div className="mx-auto max-w-md" onPointerDown={onActivate} onFocus={onActivate}>
      <form
        aria-busy="true"
        className="card-sacred min-h-[420px] p-6 sm:p-8 space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          onActivate?.();
        }}
      >
        {showTrustStrip && <TrustStrip language={language} showCheckout={false} />}
        {heading && (
          <Heading className="font-serif text-body font-bold text-text-primary">{heading}</Heading>
        )}
        {collectName && (
          <ShellField
            label={optionalName ? `${labels.yourName} ${labels.optional}` : labels.yourName}
            name="name"
            placeholder={optionalName ? labels.namePlaceholderOptional : labels.enterFullName}
          />
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ShellField label={labels.birthDate} name="dob" type="date" />
          <ShellBirthTimeField labels={labels} requireBirthTime={requireBirthTime} />
        </div>
        <ShellField
          label={labels.birthPlace}
          name="place"
          placeholder={language === "hi" ? "उदा. नई दिल्ली, भारत" : "e.g. New Delhi, India"}
        />
        <button
          type="submit"
          className="btn-sacred w-full rounded-lg py-3.5 text-body font-semibold"
        >
          {submitLabel ?? labels.defaultSubmit} &rarr;
        </button>
      </form>
    </div>
  );
}

function ShellField({
  label,
  name,
  type = "text",
  placeholder,
}: {
  label: string;
  name: string;
  type?: string;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-body-small font-semibold text-text-primary">{label}</span>
      <input
        type={type}
        name={name}
        placeholder={placeholder ?? label}
        readOnly
        aria-label={label}
        className="block h-11 w-full rounded-lg border border-border-subtle bg-surface-raised px-3 text-body text-text-primary"
      />
    </label>
  );
}

function ShellBirthTimeField({
  labels,
  requireBirthTime,
}: {
  labels: (typeof BIRTH_FORM_LABELS)[keyof typeof BIRTH_FORM_LABELS];
  requireBirthTime: boolean;
}) {
  return (
    <div>
      <label className="block">
        <span className="mb-2 block text-body-small font-semibold text-text-primary">
          {labels.birthTime}{" "}
          <span className="font-normal text-text-muted">
            {requireBirthTime ? labels.required : labels.optional}
          </span>
        </span>
        <input
          type="time"
          name="tob"
          readOnly
          aria-label={labels.birthTime}
          className="block h-11 w-full rounded-lg border border-border-subtle bg-surface-raised px-3 text-body text-text-primary"
        />
      </label>
      <p className="mt-1.5 text-caption leading-snug text-text-muted">
        {requireBirthTime ? labels.timeRequiredHint : labels.timeOptionalHint}
      </p>
      {!requireBirthTime && (
        <div className="mt-1.5">
          <button
            type="button"
            className="cursor-pointer border-0 bg-transparent p-0 text-caption font-semibold text-intent-brand underline decoration-dotted"
          >
            {labels.timeUnknownToggle}
          </button>
        </div>
      )}
    </div>
  );
}
